import dayjs from "dayjs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { ICreateSalesData, IInvoice } from "../../../@types";
import { CloseIcon, CloseIconSmall } from "../../../assets/icons";
import BackArrow from "../../../assets/icons/backArrow";
import { Calendar } from "../../../assets/icons/calendar";
import EditIcon, { EditSmallIcon } from "../../../assets/icons/EditIcon";
import PauseIcons from "../../../assets/icons/pause";
import { PauseIcon } from "../../../assets/icons/PauseIcon";
import { getInvoice } from "../../../backend-services";
import { GroupButton, SidebarDrawer, Table } from "../../../components/common";
import { CreateModal } from "../../../components/common/CreateModal";
import { MobileTable } from "../../../components/common/mobileTable";
import PageLoader from "../../../components/common/PageLoader";
import LeftNotificationPopup from "../../../components/core/NotificationPopup/LeftNotificationPopup";
import DatePickerWithShortcuts from "../../../components/DatePicker";
import { DeletePrompt } from "../../../components/Sales/invoice/ReccuringInvoice/CancelRecurring";
import EditPrompt from "../../../components/Sales/invoice/ReccuringInvoice/EditRecurring";
import { PausePrompt } from "../../../components/Sales/invoice/ReccuringInvoice/PauseRecurring";
import RecurringHistoryTable from "../../../components/Sales/invoice/Tables/RecurringHistoryTable";
import RecurringInvoiceTable from "../../../components/Sales/invoice/Tables/RecurringTable";
import { RecurringDetails } from "../../../components/Sales/recurringInvoiceDetails";
import { defaultShortcuts, TODAY } from "../../../constants/helpServices";
import { useInvoiceSearch } from "../../../hooks";
import { useUserStore } from "../../../state";
import { useInvoiceReoccurringStore } from "../../../state/useInvoiceRecurringStore";
import { isNotAccountant } from "../../../utils/isNotAccountant";
import { showToast } from "../../../utils/toastUtils";

const RecurringViewPage = () => {
  const { invoiceNo } = useParams();
  if (!invoiceNo) {
    throw new Error("Invoice Id is missing");
  }

  const location = useLocation();
  const [invoice, setInvoice] = React.useState<IInvoice | null>(null);
  const [recurringInvoice, setRecurringInvoice] = useState(null);
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [showDeletePrompt, setShowDeletePrompt] = React.useState(false);
  const [, setIsLoading] = React.useState(true);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const { roles } = useUserStore();
  const [isReoccurring, setIsReoccurring] = React.useState<boolean | null>(
    null,
  );
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>(TODAY.label);
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: "",
    endDate: "",
  });
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const currentDate = new Date();

  const handleCalendarToggle = () => {
    setShowCalendar(!showCalendar);
  };
  const [dataReady, setDataReady] = useState(false);
  const [startOfMonth, setStartOfMonth] = useState(
    new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
  );
  const [endOfMonth, setEndOfMonth] = useState(
    new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0),
  );
  const [formData, setFormData] = React.useState<ICreateSalesData>({
    // reminderType: '',
    additionalInformation: "",
    customerId: "",
    description: "",
    currency: "NGN",
    discount: 0,
    inventories: [
      {
        id: "",
        name: "",
        amount: 0,
        quantity: 0,
        rate: 0,
        variations: [
          {
            type: "",
            name: "",
            id: "",
            price: 0,
            quantity: 0,
          },
        ],
      },
    ],
    services: [{ id: "", name: "", duration: "", price: 0 }],
    invoiceDueAt: "",
    invoiceNumber: "",
    issueDate: new Date().toISOString().substring(0, 10),
    isReoccurring: false,
    isReoccurringEndDate: "",
    isReoccurringStartDate: "",
    reoccurringGroupName: "",
    status: "pending",
    tax: 0,
    isPaymentCompleted: false,
    shouldSendEmail: true,
    exchangeRate: 1,
    paymentMethod: "",
    invoiceType: "inventory",
    logoBase64: "",
  });
  const {
    data: searchResult,
    isSearching,
    handleSearch: handleInvoiceSearch,
    searchedDone,
    setSearchedDone,
  } = useInvoiceSearch();
  const handleSave = () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      isReoccurring: true,
    }));
    setShowEditModal(false);
  };

  const closeForm = () => {
    setShowEditModal(false);
  };

  const handleInputChange = (event: { target: { name: any; value: any } }) => {
    const { name, value } = event.target;
    setFormData((prevData) => {
      return { ...prevData, [name]: value };
    });
  };

  const handleDropDownChangee = (name: string, value: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const groupButtonProps = useMemo(
    () => ({
      buttons: [
        {
          text: "Edit",
          icon: <EditIcon />,
          onClick: () => setShowEditModal(true),
          textColorClass: "text-[#044E97]",
        },
        {
          text: invoice?.reoccurringStatus === "paused" ? "Resume" : "Pause",
          icon: <PauseIcons />,
          onClick: () => setShowPrompt(true),
          textColorClass: "text-[#044E97]",
        },
        {
          text: "Cancel",
          icon: <CloseIcon />,
          onClick: () => setShowDeletePrompt(true),
          textColorClass: "text-[#FB0303]",
        },
      ].filter(Boolean),
    }),
    [invoiceNo],
  );

  const getDaySuffix = (day: number) => {
    if (day >= 11 && day <= 13) {
      return day + "th";
    }

    switch (day % 10) {
      case 1:
        return day + "st";
      case 2:
        return day + "nd";
      case 3:
        return day + "rd";
      default:
        return day + "th";
    }
  };

  const reoccurringId = invoice?.reoccurringEntityId ?? "";
  const fetchInvoices = useInvoiceReoccurringStore(
    (state) => state.fetchRecurringInvoices,
  );
  const fetchMoreData = useInvoiceReoccurringStore(
    (state) => state.fetchMoreDataHistory,
  );
  const fetchData = useInvoiceReoccurringStore(
    (state) => state.fetchRecurringData,
  );
  const onSearchData = useInvoiceReoccurringStore(
    (state) => state.setSearchText,
  );
  const handleFilter = useInvoiceReoccurringStore((state) => state.setFilter);
  const handleSort = useInvoiceReoccurringStore((state) => state.handleSort);
  const invoices = useInvoiceReoccurringStore((state) => state.invoices);
  const isLoading = useInvoiceReoccurringStore((state) => state.isLoading);
  const [loadingView, setLoadingView] = useState(true);
  const range = useInvoiceReoccurringStore((state) => state.range);
  const pagination = useInvoiceReoccurringStore((state) => state.pagination);
  const searchText = useInvoiceReoccurringStore((state) => state.searchText);
  const filter = useInvoiceReoccurringStore((state) => state.filter);
  const [selected, setSelected] = useState<[Date, Date] | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const startDateRange = params.get("startDate") || "";
    const endDateRange = params.get("endDate") || "";
    setDateRange({ startDate: startDateRange, endDate: endDateRange });
  }, [location.search]);

  const handleRangeSelect = async (ranges: any) => {
    setShowCalendar(false);
    setSelected(ranges);

    const startDate = ranges[0];
    const endDate = ranges[1];
    let label = "";

    for (const shortcut of defaultShortcuts) {
      const [shortcutStart, shortcutEnd] = shortcut.range();

      if (
        startDate.getTime() === shortcutStart.getTime() &&
        endDate.getTime() === shortcutEnd.getTime()
      ) {
        label = shortcut.label;
        break;
      }
    }

    setSelectedLabel(
      label ||
        `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
    );
    setStartOfMonth(new Date(startDate));
    setEndOfMonth(new Date(endDate));
    const newStartDate = startDate?.toISOString().replace(".000Z", "");
    const newEndDate = endDate?.toISOString().replace(".000Z", "");

    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    await fetchData(newStartDate, newEndDate);
  };

  const [applyButtonClicked, setApplyButtonClicked] = useState<boolean>(false);

  const handleApplyButtonClick = async (
    startDate: Date,
    endDate: Date | null,
  ) => {
    if (endDate === null) {
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
    }
    const newStartDate = startDate?.toISOString().replace(".000Z", "");
    const newEndDate = endDate?.toISOString().replace(".000Z", "");
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    try {
      await handleRangeSelect([startDate, endDate]);
      setApplyButtonClicked(true);
      handleCalendarToggle();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (!showCalendar) {
      setSelected(null);
    }
  }, [showCalendar]);

  const fetchInvoiceData = async () => {
    setIsLoading(true);
    setLoadingView(true);
    try {
      const fetchedInvoice = await getInvoice(invoiceNo);
      setInvoice(fetchedInvoice);
      setFormData(fetchedInvoice);

      if (fetchedInvoice?.reoccurringEntityId) {
        setRecurringInvoice(fetchedInvoice.reoccurringEntityId);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showToast("An error occurred while fetching invoice data.", "error")
    } finally {
      setIsLoading(false);
      setLoadingView(false);
    }
  };
  useEffect(() => {
    fetchInvoiceData();
  }, [invoices]);

  useEffect(() => {
    const delay = (ms: number | undefined) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    const fetchFilteredData = async () => {
      if (reoccurringId) {
        try {
          await delay(2000);

          if (dateRange.startDate && dateRange.endDate) {
            await fetchData(
              reoccurringId,
              dateRange.startDate,
              dateRange.endDate,
            );
          } else {
            await fetchData(reoccurringId);
          }
        } catch (error) {
          console.error("Error fetching filtered data:", error);
        }
      }
    };

    fetchFilteredData();
  }, [reoccurringId, dateRange, fetchData]);

  return (
    <div className="px-1 540:px-3 md:px-[30px] lg:px-0 lg:ml-[5%] lg:mr-[4%]">
      {loadingView ? (
        <PageLoader></PageLoader>
      ) : (
        <div className="lg:mt-10">
          <div className="flex gap-[7px] justify-between items-center">
            <div className="flex items-center gap-[10.75px] md:gap-[26.5px]">
              <a href="/invoices/recurringList">
                <BackArrow />
              </a>
              <h1 className="font-semibold md:text-[24px]">
                Recurring Invoices
              </h1>
            </div>
            {invoice?.reoccurringStatus != "cancelled" &&
              invoice?.reoccurringStatus != "expired" && (
                <div className="md:flex md:gap-4 md:items-center">
                  <div className="hidden md:block bg-[#EDF2F5]">
                    {isNotAccountant(roles) && (
                      <GroupButton {...groupButtonProps} />
                    )}
                  </div>
                  <div></div>
                </div>
              )}

            {invoice?.reoccurringStatus != "cancelled" &&
              invoice?.reoccurringStatus != "expired" && (
                <div className="lg:hidden md:hidden flex justify-evenly items-end text-right">
                  <div
                    className="px-2 mt-2"
                    onClick={() => setShowEditModal(true)}
                  >
                    <EditSmallIcon />
                  </div>
                  <div
                    className="pl-4 mt-[em]"
                    onClick={() => setShowPrompt(true)}
                  >
                    <PauseIcons />
                  </div>
                  <div
                    className="text-[red] pl-3 pr-2 "
                    onClick={() => setShowDeletePrompt(true)}
                  >
                    <CloseIconSmall />
                  </div>
                </div>
              )}
          </div>

          <div className="w-full mt-6 md:mt-[32px] bg-white">
            <RecurringDetails invoice={invoice!} />
          </div>
          <div className="hidden lg:block md:block">
            <div className="mt-[1em] flex justify-between ">
              <h1 className="text-[20px] text-[#212121] font-semibold mt-[1.5em]">
                History of Invoices
              </h1>
            </div>
          </div>
          <div className="mt-[1em] lg:hidden md:hidden">
            <h1 className="text-[20px] text-[#212121] font-semibold mt-[1.5em]">
              History of Invoices
            </h1>
          </div>
          <div className="mt-[1.5em]">
            <div className="mt-[-1em] lg:mt-0">
              <RecurringHistoryTable
                fetchMoreData={fetchMoreData}
                onSearchData={onSearchData}
                handleSort={handleSort}
                handleFilter={handleFilter}
                handleSearch={handleInvoiceSearch}
                meta={pagination}
                invoices={invoices}
                isLoading={isLoading}
                isSearching={isSearching}
                searchResult={searchResult}
                selectedFilter={filter}
                reoccurringEntityId={invoice?.reoccurringEntityId}
                searchedDone={searchedDone}
                setSearchedDone={setSearchedDone}
              />
            </div>
          </div>
          <div className="hidden lg:block md:block">
            {showEditModal && (
              <>
                <CreateModal>
                  <EditPrompt
                    handleDropDownChange={handleDropDownChangee}
                    formData={formData}
                    setFormData={setFormData}
                    handleInputChange={handleInputChange}
                    handleSave={handleSave}
                    closeForm={closeForm}
                    setShowPrompt={setShowEditModal}
                    id={invoiceNo}
                    fetchInvoiceData={fetchInvoiceData}
                  />
                </CreateModal>
              </>
            )}
          </div>
          <div className="lg:hidden md:hidden">
            {showEditModal && (
              <>
                <EditPrompt
                  handleDropDownChange={handleDropDownChangee}
                  formData={formData}
                  setFormData={setFormData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  closeForm={closeForm}
                  setShowPrompt={setShowEditModal}
                  id={invoiceNo}
                  fetchInvoiceData={fetchInvoiceData}
                />
              </>
            )}
          </div>
          <div className="hidden lg:block md:block">
            {showPrompt && (
              <>
                <CreateModal>
                  <PausePrompt
                    setShowPrompt={setShowPrompt}
                    id={invoiceNo}
                    formData={formData}
                    fetchInvoiceData={fetchInvoiceData}
                  />
                </CreateModal>
              </>
            )}
          </div>
          <div className="lg:hidden md:hidden ">
            {showPrompt && (
              <>
                <PausePrompt
                  setShowPrompt={setShowPrompt}
                  id={invoiceNo}
                  formData={formData}
                  fetchInvoiceData={fetchInvoiceData}
                />
              </>
            )}
          </div>
          <div className="hidden md:block lg:block">
            {showDeletePrompt && (
              <>
                <CreateModal>
                  <DeletePrompt
                    setShowPrompt={setShowDeletePrompt}
                    id={invoiceNo}
                    fetchInvoiceData={fetchInvoiceData}
                  />
                </CreateModal>
              </>
            )}
          </div>
          <div className="md:hidden lg:hidden">
            {showDeletePrompt && (
              <>
                <DeletePrompt
                  setShowPrompt={setShowDeletePrompt}
                  id={invoiceNo}
                  fetchInvoiceData={fetchInvoiceData}
                />
              </>
            )}
          </div>
        </div>
      )}
      {showCalendar && (
        <>
          <div className="bg-gray-900 bg-opacity-50 fixed top-0 right-0 left-0 z-50 h-[100vh]">
            <div className="hidden md:flex justify-center items-center h-full w-full">
              <DatePickerWithShortcuts
                onRangeSelect={handleRangeSelect}
                selected={selected}
                onClose={handleCalendarToggle}
                onApplyButtonClick={(startDate, endDate) =>
                  handleApplyButtonClick(startDate, endDate)
                }
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export { RecurringViewPage };
